// activeWorkspacePointer.js — the ONE owner of the client "active workspace"
// pointer (MULTI_WORKSPACE_TABS.md §6e/§6h/§5c-B). Converges the 4 read + 3 write
// sites that hand-rolled the `localStorage.activeWorkspace`-wins precedence
// (bootShell, auth.js, switchers) onto a single accessor, killing the drift the
// design flagged, and switches the storage backing per the rollout flag:
//
//   MWT OFF (default) → shared `localStorage.activeWorkspace` — every tab reads
//     the same value. Byte-identical to today.
//   MWT ON            → PER-TAB `sessionStorage.activeWorkspace`, seeded from
//     localStorage for a fresh tab, and at boot overridden by the tab's URL
//     (`/w/<org-slug>`, resolved via `resolveActiveWorkspace`). localStorage is
//     kept as the "last default" seed so new tabs open where you last were.
//
// The SDK is scoped separately (boot calls `sdk.updateContext({activeWorkspaceId})`
// — client-only, per-tab; services thread it as the Phase-0 `?workspaceId=` param).
// This module owns ONLY the client mirror + the resolution precedence.
//
// Pure-ish module, direct ESM import (workspacePrefix.js / guestRoutes.js pattern).

import { isMwtEnabled } from './mwtFlag.js'
import {
  getWorkspacePrefixSlug,
  getWorkspaceQueryParam,
  getWorkspaceRawSegments
} from './workspacePrefix.js'

// READ the tab's active-workspace pointer id (string) or null.
//
// FA204 / frank trap — KEY/_local/_session/_path/_search below are each
// re-declared as self-contained locals inside every exported function that
// needs them (a bare module-local read is exactly as unresolvable
// post-frank as a bare sibling-export read — see longPress.js for the
// simple case, notificationScope.js for the duplicated-body case).
export const getActiveWorkspacePointer = () => {
  const KEY = 'activeWorkspace'
  const local = () => {
    try {
      return globalThis.localStorage || null
    } catch {
      return null
    }
  }
  const session = () => {
    try {
      return globalThis.sessionStorage || null
    } catch {
      return null
    }
  }

  if (!isMwtEnabled()) return local()?.getItem(KEY) || null
  const ss = session()
  const own = ss?.getItem(KEY)
  if (own) return own
  // Fresh tab: seed from the shared localStorage default, then own it per-tab so
  // subsequent reads are stable even if another tab later moves the seed.
  const seed = local()?.getItem(KEY) || null
  if (seed && ss) {
    try {
      ss.setItem(KEY, seed)
    } catch {}
  }
  return seed
}

// WRITE the tab's active-workspace pointer.
//   MWT ON  → sessionStorage ONLY (this tab). Deliberately does NOT touch
//     localStorage: ANY write to the shared key fires the native `storage` event
//     and re-converges every OTHER tab (wireGlobalListeners) — the exact thing
//     per-tab isolation must avoid before Phase 4 gates that listener. The shared
//     localStorage "default" (what fresh tabs seed from) advances only via a
//     deliberate global switch (sdk.switchWorkspace), never an established tab's
//     per-tab pointer write. MWT OFF → localStorage only (today).
export const setActiveWorkspacePointer = (id) => {
  const KEY = 'activeWorkspace'
  const local = () => {
    try {
      return globalThis.localStorage || null
    } catch {
      return null
    }
  }
  const session = () => {
    try {
      return globalThis.sessionStorage || null
    } catch {
      return null
    }
  }

  if (!id) return
  const v = String(id)
  if (!isMwtEnabled()) {
    try {
      local()?.setItem(KEY, v)
    } catch {}
    return
  }
  try {
    session()?.setItem(KEY, v)
  } catch {}
}

// CLEAR from BOTH stores — logout / clearAuthStorage must call this (the per-tab
// sessionStorage key would otherwise survive a logout on the same tab).
export const clearActiveWorkspacePointer = () => {
  const KEY = 'activeWorkspace'
  const local = () => {
    try {
      return globalThis.localStorage || null
    } catch {
      return null
    }
  }
  const session = () => {
    try {
      return globalThis.sessionStorage || null
    } catch {
      return null
    }
  }

  try {
    local()?.removeItem(KEY)
  } catch {}
  try {
    session()?.removeItem(KEY)
  } catch {}
}

// org id → its HQ workspace id, from a workspaces list. HQ convention is
// `type:'shared'` (`'hq'` reserved), mirroring AppOrgSwitcher._homeWorkspace.
const _hqWorkspaceId = (orgId, workspaces) => {
  if (!Array.isArray(workspaces)) return null
  const oid = String(orgId)
  const mine = workspaces.filter((w) => w && String(w.org_id ?? w.organization ?? '') === oid)
  const hq = mine.find((w) => w.type === 'hq') || mine.find((w) => w.type === 'shared') || mine[0]
  return hq ? String(hq.id ?? hq._id ?? '') || null : null
}

// Find one workspace INSIDE an org by its URL handle. Scoping to the org is
// what makes slugs safe here — Workspace.slug is unique per org, not globally.
// Order mirrors how readable each handle is: `slug` (human, optional on the
// model), then `key` (`ws_<nanoid>`, always present and globally unique — the
// fallback for slug-less workspaces), then the raw id so a pasted id resolves.
const _findInOrg = (orgId, workspaces, wanted) => {
  if (!Array.isArray(workspaces) || !wanted) return null
  const oid = String(orgId)
  const want = String(wanted).toLowerCase()
  const mine = workspaces.filter((w) => w && String(w.org_id ?? w.organization ?? '') === oid)
  const eq = (v) => String(v ?? '').toLowerCase() === want
  const hit =
    mine.find((w) => w.slug && eq(w.slug)) ||
    mine.find((w) => w.key && eq(w.key)) ||
    mine.find((w) => eq(w.id ?? w._id))
  return hit ? String(hit.id ?? hit._id ?? '') || null : null
}

// Public face of the matcher — the shell's workspace-handle resolver
// (registerWorkspaceHandleResolver in workspacePrefix.js) answers "is path
// segment 2 a workspace of this org?" with EXACTLY the boot matcher's rule,
// so URL parsing and boot resolution can never disagree on what counts as a
// handle.
export const findWorkspaceInOrg = _findInOrg

// The handle a workspace should be addressed by in the URL — same precedence.
export const workspaceUrlHandle = (ws) => {
  if (!ws) return null
  return String(ws.slug || ws.key || ws.id || ws._id || '') || null
}

// Resolve the workspace this tab should scope to, honoring URL precedence.
//   MWT ON : URL `/w/<org-slug>?ws=<workspace-slug>` → URL org HQ → per-tab
//            pointer → claim → null.
//   MWT OFF: pointer (localStorage) → claim → null (today's precedence).
// Inputs are the boot lists off the state root: `orgs` (all orgs, each `{id,slug}`),
// `workspaces` (active-org-scoped) and optional `orgWorkspaces` (map `{orgId:[ws]}`
// — the switcher's cross-org source). Returns `{ workspaceId, orgId, orgSlug, source }`;
// `orgId`/`orgSlug` are set only when a URL slug drove the pick (so boot can also
// re-scope the org + fetch that org's workspaces if they aren't loaded yet).
// FA204 / frank trap — self-contained inline copies of _path/_search/
// _findInOrg/_hqWorkspaceId/getActiveWorkspacePointer below (a bare
// module-local or sibling-export read is exactly as unresolvable
// post-frank as either alone); the exports above stay live for their
// genuine external consumers.
export const resolveActiveWorkspace = ({
  url,
  search,
  orgs,
  workspaces,
  orgWorkspaces,
  claim
} = {}) => {
  const path = () => {
    try {
      return globalThis.location?.pathname || ''
    } catch {
      return ''
    }
  }
  const searchStr = () => {
    try {
      return globalThis.location?.search || ''
    } catch {
      return ''
    }
  }
  const findInOrg = (orgId, wsList, wanted) => {
    if (!Array.isArray(wsList) || !wanted) return null
    const oid = String(orgId)
    const want = String(wanted).toLowerCase()
    const mine = wsList.filter((w) => w && String(w.org_id ?? w.organization ?? '') === oid)
    const eq = (v) => String(v ?? '').toLowerCase() === want
    const hit =
      mine.find((w) => w.slug && eq(w.slug)) ||
      mine.find((w) => w.key && eq(w.key)) ||
      mine.find((w) => eq(w.id ?? w._id))
    return hit ? String(hit.id ?? hit._id ?? '') || null : null
  }
  const hqWorkspaceId = (orgId, wsList) => {
    if (!Array.isArray(wsList)) return null
    const oid = String(orgId)
    const mine = wsList.filter((w) => w && String(w.org_id ?? w.organization ?? '') === oid)
    const hq = mine.find((w) => w.type === 'hq') || mine.find((w) => w.type === 'shared') || mine[0]
    return hq ? String(hq.id ?? hq._id ?? '') || null : null
  }
  const activeWorkspacePointer = () => {
    const KEY = 'activeWorkspace'
    const local = () => {
      try {
        return globalThis.localStorage || null
      } catch {
        return null
      }
    }
    const session = () => {
      try {
        return globalThis.sessionStorage || null
      } catch {
        return null
      }
    }
    if (!isMwtEnabled()) return local()?.getItem(KEY) || null
    const ss = session()
    const own = ss?.getItem(KEY)
    if (own) return own
    const seed = local()?.getItem(KEY) || null
    if (seed && ss) {
      try {
        ss.setItem(KEY, seed)
      } catch {}
    }
    return seed
  }

  if (isMwtEnabled()) {
    const slug = getWorkspacePrefixSlug(url || path())
    if (slug && Array.isArray(orgs)) {
      const org = orgs.find((o) => o && o.slug === slug)
      if (org) {
        const fromMap = orgWorkspaces && orgWorkspaces[org.id]
        const list = Array.isArray(fromMap) ? fromMap : workspaces
        // Scheme A: `/w/<org>/<ws-handle>/…` — segment 2 names WHICH workspace
        // when it matches one of this org's workspaces (data-driven, checked
        // against the org's own list right here — no dependence on the shell's
        // handle resolver, which may not be registered at boot). An
        // unrecognized segment is a route on the HQ — exactly the legacy
        // reading, so `/w/icca/documents` stays the documents page unless the
        // org really has a workspace with that handle.
        const { seg2 } = getWorkspaceRawSegments(url || path())
        if (seg2) {
          const pathPick = findInOrg(org.id, list, seg2) || findInOrg(org.id, workspaces, seg2)
          if (pathPick) {
            return {
              workspaceId: pathPick,
              orgId: String(org.id),
              orgSlug: slug,
              source: 'url-path-ws'
            }
          }
          // `default` that no workspace of the org claims names the org's MAIN
          // workspace — the server's meaning (workspaceSlug.js: the HQ slug
          // convention) — never the tab's last-used one (Nika 2026-10-18). Same
          // HQ rule as the bare-org fallback below. Mirrored in the warm boot
          // snapshot's scope match (workspace bootSnapshot.js).
          if (String(seg2).toLowerCase() === 'default') {
            const hqId = hqWorkspaceId(org.id, list) || hqWorkspaceId(org.id, workspaces)
            if (hqId) {
              return {
                workspaceId: hqId,
                orgId: String(org.id),
                orgSlug: slug,
                source: 'url-default'
              }
            }
          }
        }
        // Legacy `?ws=` — parsed forever so old links keep resolving; the
        // canonicalizer (syncWorkspaceUrl) rewrites it to path form on the
        // first state sync. Matched on slug first (what the address bar
        // carries), id second (paste-an-id still works).
        const wanted = getWorkspaceQueryParam(search != null ? search : searchStr())
        if (wanted) {
          const pick = findInOrg(org.id, list, wanted) || findInOrg(org.id, workspaces, wanted)
          if (pick) {
            return { workspaceId: pick, orgId: String(org.id), orgSlug: slug, source: 'url-ws' }
          }
        }
        // No `?ws=` — the URL names the org only, so it cannot say WHICH of its
        // workspaces. Before falling back to HQ, honour the tab's own pointer
        // when it points INSIDE this org: it is strictly more specific than the
        // org, and it is what the user last chose. Skipping this is why a reload
        // of a two-workspace org always bounced back to the HQ workspace even
        // though the tab clearly knew which one it had been in. A pointer into a
        // DIFFERENT org is correctly ignored — the URL wins on the org.
        const ptrInOrg = findInOrg(org.id, list, activeWorkspacePointer())
        if (ptrInOrg) {
          return {
            workspaceId: ptrInOrg,
            orgId: String(org.id),
            orgSlug: slug,
            source: 'url-pointer'
          }
        }
        const wsId = hqWorkspaceId(org.id, list) || hqWorkspaceId(org.id, workspaces)
        if (wsId) return { workspaceId: wsId, orgId: String(org.id), orgSlug: slug, source: 'url' }
        // P0 tenant isolation (tickets/fable.md 2026-08-14 — "/w/natali/…/notes
        // RENDERS BELLFORGE"): the URL names a MEMBER org, but none of its
        // workspaces are resolvable yet (cross-org cold deep-link before the
        // target org's list loads). This used to FALL THROUGH to the tab
        // pointer — a workspace in a DIFFERENT org — so the whole boot scoped
        // to the pointer's tenant while the address bar named this one: the
        // exact cross-tenant render Nika reported. The URL is the source of
        // truth for scope; the pointer is an accelerator that must never win
        // over a URL-named member org. Return the org pinned with the
        // workspace deliberately unresolved — bootShell skips the
        // workspace-keyed seeds/pointer write for a null workspaceId, scopes
        // the org fetch to THIS org, and the auth tick's listWorkspaces
        // fallback resolves the workspace within one boot. A pointer into a
        // NON-member/unknown slug still falls through below (this session
        // cannot scope to an org it cannot see; reconcileMwtOrgFromUrl owns
        // the honest URL rescope for that case).
        return {
          workspaceId: null,
          orgId: String(org.id),
          orgSlug: slug,
          source: 'url-org-unresolved'
        }
      }
    }
  }
  const ptr = activeWorkspacePointer()
  if (ptr) return { workspaceId: ptr, orgId: null, orgSlug: null, source: 'pointer' }
  if (claim) return { workspaceId: String(claim), orgId: null, orgSlug: null, source: 'claim' }
  return { workspaceId: null, orgId: null, orgSlug: null, source: 'none' }
}
